import React, { useEffect, useState } from 'react'
import {
    Autocomplete,
    Box,
    Button,
    CardContent,
    Checkbox,
    createFilterOptions,
    Grid,
    TableCell,
    TextField,
    Typography
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import styled from '@emotion/styled';
import { CustomTable } from '../../components/CustomTable';
import {
    getAllSchools,
    GetMobileMenuList,
    SchoolMobileMenuSaveUpdate,
    SchoolMobileParentMenuList,
} from '../../api/auth/authService';
import { getSchoolByID } from '../../api/SchoolService/SchoolService';

function ParentAppMenu() {
    const [adminCheckedMap, setAdminCheckedMap] = useState({});
    const [initialCheckedMap, setInitialCheckedMap] = useState({});
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState("1000");
    const [schoolProfile, setSchoolProfile] = useState(null);

    const schoolProfileData = schoolProfile || {};

    const [flatAdminRows, setFlatAdminRows] = useState([]);
    const [adminParentMap, setAdminParentMap] = useState({});

    const tableHeaders = ['Menus', 'Status'];

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        fontSize: '0.85rem',
        whiteSpace: 'nowrap',
        borderBottom: '1px solid #efefef',
    }));

    const fetchSchools = async () => {
        try {
            const response = await getAllSchools(page, rowsPerPage);

            if (response.success && response.data?.schools) {
                const fetchedSchools = response.data.schools.map(school => ({
                    id: school.schoolID,
                    schoolCode: school.schoolCode || "",
                    SchoolName: school.schoolName,
                    BranchName: school.schoolBranchName || "",
                    Address: school.address,
                    City: school.city,
                    State: school.state,
                }));

                setSchools(fetchedSchools);
            } else {
                setSchools([]);
            }
        } catch (error) {
            setSchools([]);
        }
    };

    const filterOptions = createFilterOptions({
        matchFrom: 'any',
        stringify: (option) => `${option.SchoolName || ""} ${option.BranchName || ""}`,
    });

    const fetchSchoolProfile = async () => {
        if (!selectedSchool?.id) {
            setSchoolProfile(null);
            return;
        }
        try {
            const response = await getSchoolByID(selectedSchool.id);
            if (response?.success && response?.data) {
                setSchoolProfile(response.data);
            } else {
                console.warn("Failed to fetch school profile", "warning");
                setSchoolProfile(null);
            }
        } catch (error) {
            console.error("Error fetching school profile", error);
            setSchoolProfile(null);
        }
    };


    const fetchSchoolMenus = async () => {
        if (!selectedSchool?.id) {
            setFlatAdminRows([]);
            setAdminCheckedMap({});
            return;
        }

        try {
            const allMenusResponse = await GetMobileMenuList();

            if (!allMenusResponse?.data) {
                return;
            }

            const allMenuData = Array.isArray(allMenusResponse.data) ? allMenusResponse.data : [];
            const newFlatRows = [];
            const newCheckedMap = {};

            allMenuData.forEach(menu => {
                const menuId = String(menu.id);
                newFlatRows.push({
                    id: menuId,
                    label: menu.menuName,
                    type: 'group',
                    group: null
                });
                newCheckedMap[menuId] = true;
            });

            const hiddenMenusResponse = await SchoolMobileParentMenuList(selectedSchool.id);

            if (hiddenMenusResponse?.data) {
                const hiddenMenuData = Array.isArray(hiddenMenusResponse.data)
                    ? hiddenMenusResponse.data
                    : [];

                hiddenMenuData.forEach(menu => {
                    const menuId = String(menu.menuId || menu.id);

                    if (newCheckedMap.hasOwnProperty(menuId)) {
                        newCheckedMap[menuId] = Boolean(menu.isVisible);
                    }
                });
            }

            setFlatAdminRows(newFlatRows);
            setAdminCheckedMap(newCheckedMap);
            setInitialCheckedMap({ ...newCheckedMap });
            setAdminParentMap({});
        } catch (error) {
            console.error("Error fetching menus", error);
        }
    };

    const handleSubmit = async () => {
        if (!selectedSchool?.id) {
            console.warn("School ID missing");
            return;
        }

        const menuList = flatAdminRows
            .filter(row =>
                adminCheckedMap[row.id] !== initialCheckedMap[row.id] || !adminCheckedMap[row.id]
            )
            .map(row => {
                return {
                    menuId: Number(row.id),
                    isVisible: Boolean(adminCheckedMap?.[row.id]),
                };
            });

        const payload = {
            schoolID: selectedSchool.id,
            academicYearId: null,
            modifiedBy: null,
            menuList: menuList,
        };

        try {
            const response = await SchoolMobileMenuSaveUpdate(payload);
            if (response?.success) {
                alert("Menus updated successfully!");
                await fetchSchoolMenus();
            } else {
                alert("Failed to update menus.");
            }
        } catch (error) {
            console.error("Error updating menus", error);
            alert("Error updating menus.");
        }
    };


    useEffect(() => {
        fetchSchoolProfile();
        fetchSchoolMenus();
    }, [selectedSchool]);

    useEffect(() => {
        fetchSchools();
    }, [page, rowsPerPage]);

    return (
        <>
            <Box
                sx={{
                    background: "#fff",
                    borderRadius: 2,
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "center" },
                        borderRadius: "8px",
                        background: "linear-gradient(135deg, #f5f9ff, #eef3ff)",
                        border: "1px solid #e0e7ff",
                        gap: 2,
                        p: 2,
                    }}
                >
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: "#1e3a8a", lineHeight: 1.2 }}>
                            Parent App Menu
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                            Track and manage all school leads
                        </Typography>
                    </Box>

                    <Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Box display='flex' gap={2} sx={{ alignItems: "center" }}>
                                <Box width={700}>
                                    <Autocomplete
                                        size="small"
                                        options={schools}
                                        filterOptions={filterOptions}
                                        getOptionLabel={(option) => {
                                            if (!option) return "";
                                            if (typeof option === "string") return option;
                                            const branchString = option.BranchName ? ` - ${option.BranchName}` : "";
                                            return `${option.SchoolName || ""}${branchString}`;
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.id}>
                                                <div>
                                                    <strong>{option.SchoolName}</strong>
                                                    {option.BranchName && <b> - {option.BranchName}</b>}
                                                </div>
                                            </li>
                                        )}
                                        value={selectedSchool}
                                        onChange={(event, newValue) => {
                                            setSelectedSchool(newValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="School Name"
                                                variant="outlined"
                                                sx={{ bgcolor: "#fff" }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </LocalizationProvider>
                    </Box>
                </Box>
            </Box>


            {schoolProfile && (
                <Grid container spacing={2} mt={2}>
                    {/* School Detail Section */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <CardContent
                            sx={{
                                pt: 0,
                                background: "#fff",
                                borderRadius: 2,
                                p: 2,
                            }}
                            px={1}
                        >
                            <Box display={'flex'} flexWrap={{ xs: 'wrap', sm: 'inherit' }} gap={2}>
                                <Grid container rowSpacing={2} columnSpacing={2}>

                                    {/* School Name */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                School Name :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolName || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Branch Name */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Branch Name :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolBranchName || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* School Code */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                School Code :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.schoolCode || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* State */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                State :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.state || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* City */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                City :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.city || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Pin Code */}
                                    <Grid item size={{ xs: 12, sm: 6 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Pin Code :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.pinCode || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    {/* Address */}
                                    <Grid item size={{ xs: 12 }}>
                                        <Box sx={{ alignItems: "flex-start", flexWrap: "nowrap", gap: 1 }}>
                                            <Typography variant="body2" sx={{ color: "text.secondary", flexShrink: 0 }}>
                                                Address :
                                            </Typography>
                                            <Typography variant="body2" fontWeight={500} sx={{
                                                width: "100%", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "normal", lineHeight: 1.5,
                                            }}>
                                                {schoolProfileData?.address || "-"}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                </Grid>
                            </Box>
                        </CardContent>
                    </Grid>

                    {/* Menu Status Section */}
                    <Grid size={{ xs: 12, lg: 6 }}>
                        <CardContent
                            sx={{
                                background: "#fff",
                                borderRadius: 2,
                                p: 2,
                                pb: 5,
                            }}
                        >
                            <Box sx={{ border: '1px solid #e0e7ff', mb: 2, borderRadius: '8px', overflow: 'hidden' }}>
                                <CustomTable
                                    headers={tableHeaders}
                                    rows={flatAdminRows}
                                    renderRow={(row) => (
                                        <>
                                            <StyledTableCell sx={{ fontWeight: 600 }}>
                                                {row.label}
                                            </StyledTableCell>
                                            <StyledTableCell sx={{ padding: "4px 16px", borderLeft: '1px solid #ccc' }}>
                                                <Checkbox
                                                    size="small"
                                                    checked={Boolean(adminCheckedMap?.[row.id])}
                                                    onChange={() => {
                                                        setAdminCheckedMap(prev => ({
                                                            ...prev,
                                                            [row.id]: !prev[row.id]
                                                        }));
                                                    }}
                                                />
                                            </StyledTableCell>
                                        </>
                                    )}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    onClick={handleSubmit}
                                    variant="contained"
                                    sx={{
                                        boxShadow: "none",
                                        whiteSpace: "nowrap",
                                        "&:hover": { boxShadow: "none" },
                                    }}
                                >
                                    Submit
                                </Button>
                            </Box>
                        </CardContent>
                    </Grid>
                </Grid>
            )}
        </>
    )
}

export default ParentAppMenu
